import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  InlineNotification,
  Modal,
  Search,
  Select,
  SelectItem,
  Tag,
  TextInput,
  Toggle,
} from "@carbon/react";
import {
  Add,
  Edit,
  Hospital,
  Location,
  TrashCan,
  UserMultiple,
  View,
} from "@carbon/react/icons";
import {
  createAdminId,
  importDistrictNames,
  loadAdminData,
  saveAdminData,
} from "./adminDataStore";

const entitySections = [
  { id: "mobileUsers", label: "Mobile app users", singular: "mobile user", icon: UserMultiple },
  { id: "districts", label: "Districts", singular: "district", icon: Location },
  { id: "dutyStations", label: "Duty stations", singular: "duty station", icon: Hospital },
];

const emptyForms = {
  districts: { name: "", active: true },
  dutyStations: { name: "", districtId: "", type: "0", active: true },
  mobileUsers: { fullName: "", username: "", phone: "", districtId: "", dutyStationId: "", active: true },
};

const getSection = entity => entitySections.find(section => section.id === entity);

const AdminDataManager = () => {
  const [data, setData] = useState(loadAdminData);
  const [activeEntity, setActiveEntity] = useState("mobileUsers");
  const [searchTerm, setSearchTerm] = useState("");
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(emptyForms.mobileUsers);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (data.districts.length > 0) {
      return undefined;
    }

    const controller = new AbortController();

    const importDistricts = async () => {
      try {
        const response = await fetch(
          "https://sgbv.caringsoulshomecare.com/api/?cmd=getDistricts",
          { signal: controller.signal }
        );
        const payload = await response.json();
        if (payload.status === "ok" && Array.isArray(payload.results)) {
          setData(currentData => {
            const nextData = importDistrictNames(
              currentData,
              payload.results.map(item => item.district).filter(Boolean)
            );
            saveAdminData(nextData);
            return nextData;
          });
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          setNotification({ kind: "warning", title: "District import unavailable", subtitle: "You can still add districts manually." });
        }
      }
    };

    importDistricts();
    return () => controller.abort();
  }, [data.districts.length]);

  const districtById = useMemo(
    () => Object.fromEntries(data.districts.map(district => [district.id, district])),
    [data.districts]
  );
  const stationById = useMemo(
    () => Object.fromEntries(data.dutyStations.map(station => [station.id, station])),
    [data.dutyStations]
  );

  const activeSection = getSection(activeEntity);
  const records = data[activeEntity];
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredRecords = records.filter(record =>
    Object.values(record).some(value =>
      String(value ?? "").toLowerCase().includes(normalizedSearch)
    )
  );

  const updateData = nextData => {
    setData(saveAdminData(nextData));
  };

  const openEditor = (mode, entity, record = null) => {
    setActiveEntity(entity);
    setEditor({ mode, entity, record });
    setForm(record ? { ...record } : { ...emptyForms[entity] });
    setNotification(null);
    setFormError("");
  };

  const updateForm = (field, value) => {
    setForm(currentForm => {
      const nextForm = { ...currentForm, [field]: value };
      if (field === "districtId" && editor?.entity === "mobileUsers") {
        nextForm.dutyStationId = "";
      }
      return nextForm;
    });
  };

  const validateForm = () => {
    if (activeEntity === "districts") {
      if (!form.name.trim()) return "District name is required.";
      const duplicate = data.districts.some(item =>
        item.id !== editor?.record?.id && item.name.trim().toLowerCase() === form.name.trim().toLowerCase()
      );
      if (duplicate) return "A district with this name already exists.";
    }

    if (activeEntity === "dutyStations") {
      if (!form.name.trim() || !form.districtId) return "Duty-station name and district are required.";
    }

    if (activeEntity === "mobileUsers") {
      if (!form.fullName.trim() || !form.username.trim() || !form.districtId || !form.dutyStationId) {
        return "Full name, username, district, and duty station are required.";
      }
      const duplicate = data.mobileUsers.some(item =>
        item.id !== editor?.record?.id && item.username.trim().toLowerCase() === form.username.trim().toLowerCase()
      );
      if (duplicate) return "This mobile username is already in use.";
    }

    return null;
  };

  const handleSave = () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const record = {
      ...form,
      id: editor.record?.id || createAdminId(activeEntity),
      name: form.name?.trim(),
      fullName: form.fullName?.trim(),
      username: form.username?.trim(),
      phone: form.phone?.trim(),
    };
    const nextRecords = editor.mode === "edit"
      ? records.map(item => item.id === record.id ? record : item)
      : [...records, record];

    updateData({ ...data, [activeEntity]: nextRecords });
    setEditor(null);
    setNotification({ kind: "success", title: "Record saved", subtitle: `${activeSection.singular[0].toUpperCase()}${activeSection.singular.slice(1)} details were saved locally.` });
  };

  const requestDelete = (entity, record) => {
    if (entity === "districts") {
      const linkedStations = data.dutyStations.filter(station => station.districtId === record.id).length;
      if (linkedStations > 0) {
        setNotification({ kind: "error", title: "District cannot be deleted", subtitle: `Remove or reassign ${linkedStations} linked duty station${linkedStations === 1 ? "" : "s"} first.` });
        return;
      }
    }

    if (entity === "dutyStations") {
      const linkedUsers = data.mobileUsers.filter(user => user.dutyStationId === record.id).length;
      if (linkedUsers > 0) {
        setNotification({ kind: "error", title: "Duty station cannot be deleted", subtitle: `Remove or reassign ${linkedUsers} linked mobile user${linkedUsers === 1 ? "" : "s"} first.` });
        return;
      }
    }

    setDeleteTarget({ entity, record });
  };

  const confirmDelete = () => {
    const section = getSection(deleteTarget.entity);
    updateData({
      ...data,
      [deleteTarget.entity]: data[deleteTarget.entity].filter(item => item.id !== deleteTarget.record.id),
    });
    setDeleteTarget(null);
    setNotification({ kind: "success", title: "Record deleted", subtitle: `The ${section.singular} was removed from the local administration store.` });
  };

  const getRecordName = record => record.fullName || record.name;
  const stationTypeLabel = type => String(type) === "0" ? "Health facility" : "Police station";
  const userStations = data.dutyStations.filter(station => station.districtId === form.districtId);

  const renderRows = () => {
    if (filteredRecords.length === 0) {
      return <tr><td colSpan="6" className="admin-empty-row">No {activeSection.label.toLowerCase()} found.</td></tr>;
    }

    return filteredRecords.map(record => (
      <tr key={record.id}>
        {activeEntity === "mobileUsers" && <>
          <td><strong>{record.fullName}</strong><span className="admin-cell-secondary">@{record.username}</span></td>
          <td>{record.phone || "—"}</td>
          <td>{districtById[record.districtId]?.name || "Unknown district"}</td>
          <td>{stationById[record.dutyStationId]?.name || "Unknown station"}</td>
        </>}
        {activeEntity === "districts" && <>
          <td><strong>{record.name}</strong>{record.imported && <span className="admin-cell-secondary">Imported from dashboard API</span>}</td>
          <td>{data.dutyStations.filter(station => station.districtId === record.id).length}</td>
        </>}
        {activeEntity === "dutyStations" && <>
          <td><strong>{record.name}</strong></td>
          <td>{districtById[record.districtId]?.name || "Unknown district"}</td>
          <td><Tag type={String(record.type) === "0" ? "green" : "purple"}>{record.type} · {stationTypeLabel(record.type)}</Tag></td>
        </>}
        <td><Tag type={record.active ? "green" : "gray"}>{record.active ? "Active" : "Inactive"}</Tag></td>
        <td>
          <div className="admin-row-actions">
            <Button hasIconOnly kind="ghost" size="sm" renderIcon={View} iconDescription="View" onClick={() => openEditor("view", activeEntity, record)} />
            <Button hasIconOnly kind="ghost" size="sm" renderIcon={Edit} iconDescription="Edit" onClick={() => openEditor("edit", activeEntity, record)} />
            <Button hasIconOnly kind="danger--ghost" size="sm" renderIcon={TrashCan} iconDescription="Delete" onClick={() => requestDelete(activeEntity, record)} />
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <section className="admin-management" aria-labelledby="record-management-title">
      <div className="admin-management-heading">
        <div><span>Data administration</span><h2 id="record-management-title">Record management</h2><p>Add, review, update, and remove mobile users, districts, and duty stations.</p></div>
        <Tag type="warm-gray">Temporary browser store</Tag>
      </div>

      <InlineNotification
        kind="warning"
        lowContrast
        hideCloseButton
        title="Backend CRUD endpoints required"
        subtitle="These management records currently persist in this browser only. Connect authenticated API endpoints before using them to provision the mobile application."
      />

      {notification && (
        <InlineNotification
          kind={notification.kind}
          lowContrast
          title={notification.title}
          subtitle={notification.subtitle}
          onCloseButtonClick={() => setNotification(null)}
        />
      )}

      <div className="admin-entity-tabs" role="tablist" aria-label="Managed record types">
        {entitySections.map(section => {
          const Icon = section.icon;
          return (
            <button
              type="button"
              key={section.id}
              role="tab"
              aria-selected={activeEntity === section.id}
              className={activeEntity === section.id ? "is-active" : ""}
              onClick={() => { setActiveEntity(section.id); setSearchTerm(""); setNotification(null); }}
            >
              <Icon size={18} />
              <span>{section.label}</span>
              <strong>{data[section.id].length}</strong>
            </button>
          );
        })}
      </div>

      <div className="admin-entity-panel">
        <div className="admin-entity-toolbar">
          <Search id="admin-record-search" labelText={`Search ${activeSection.label}`} placeholder={`Search ${activeSection.label.toLowerCase()}`} value={searchTerm} onChange={event => setSearchTerm(event.target.value)} />
          <Button renderIcon={Add} onClick={() => openEditor("add", activeEntity)}>Add {activeSection.singular}</Button>
        </div>

        <div className="admin-table-scroll">
          <table className="admin-record-table">
            <thead><tr>
              {activeEntity === "mobileUsers" && <><th>User</th><th>Phone</th><th>District</th><th>Duty station</th></>}
              {activeEntity === "districts" && <><th>District</th><th>Duty stations</th></>}
              {activeEntity === "dutyStations" && <><th>Duty station</th><th>District</th><th>Type</th></>}
              <th>Status</th><th><span className="sr-only">Actions</span></th>
            </tr></thead>
            <tbody>{renderRows()}</tbody>
          </table>
        </div>
      </div>

      <Modal
        open={Boolean(editor && editor.mode !== "view")}
        modalHeading={`${editor?.mode === "edit" ? "Edit" : "Add"} ${getSection(editor?.entity)?.singular || "record"}`}
        primaryButtonText={editor?.mode === "edit" ? "Save changes" : "Add record"}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSave}
        onRequestClose={() => setEditor(null)}
      >
        <div className="admin-editor-form">
          {formError && <InlineNotification kind="error" lowContrast hideCloseButton title="Cannot save record" subtitle={formError} />}
          {editor?.entity === "districts" && <TextInput id="district-name" labelText="District name" value={form.name || ""} onChange={event => updateForm("name", event.target.value)} required />}
          {editor?.entity === "dutyStations" && <>
            <TextInput id="station-name" labelText="Duty-station name" value={form.name || ""} onChange={event => updateForm("name", event.target.value)} required />
            <Select id="station-district" labelText="District" value={form.districtId || ""} onChange={event => updateForm("districtId", event.target.value)} required><SelectItem value="" text="Choose a district" />{data.districts.map(district => <SelectItem key={district.id} value={district.id} text={district.name} />)}</Select>
            <Select id="station-type" labelText="Duty-station type" value={String(form.type ?? "0")} onChange={event => updateForm("type", event.target.value)}><SelectItem value="0" text="0 — Health facility" /><SelectItem value="1" text="1 — Police station" /></Select>
          </>}
          {editor?.entity === "mobileUsers" && <>
            <TextInput id="mobile-user-name" labelText="Full name" value={form.fullName || ""} onChange={event => updateForm("fullName", event.target.value)} required />
            <TextInput id="mobile-username" labelText="Username" value={form.username || ""} onChange={event => updateForm("username", event.target.value)} required />
            <TextInput id="mobile-phone" labelText="Phone number" value={form.phone || ""} onChange={event => updateForm("phone", event.target.value)} />
            <Select id="mobile-user-district" labelText="District" value={form.districtId || ""} onChange={event => updateForm("districtId", event.target.value)} required><SelectItem value="" text="Choose a district" />{data.districts.map(district => <SelectItem key={district.id} value={district.id} text={district.name} />)}</Select>
            <Select id="mobile-user-station" labelText="Duty station" value={form.dutyStationId || ""} onChange={event => updateForm("dutyStationId", event.target.value)} required disabled={!form.districtId}><SelectItem value="" text="Choose a duty station" />{userStations.map(station => <SelectItem key={station.id} value={station.id} text={`${station.name} (${station.type})`} />)}</Select>
          </>}
          {editor && <Toggle id="record-active" labelText="Record status" labelA="Inactive" labelB="Active" toggled={form.active !== false} onToggle={value => updateForm("active", value)} />}
        </div>
      </Modal>

      <Modal open={Boolean(editor?.mode === "view")} passiveModal modalHeading={`${getSection(editor?.entity)?.singular || "Record"} details`} onRequestClose={() => setEditor(null)}>
        {editor?.record && <dl className="admin-record-details">
          <div><dt>Name</dt><dd>{getRecordName(editor.record)}</dd></div>
          {editor.entity === "mobileUsers" && <><div><dt>Username</dt><dd>{editor.record.username}</dd></div><div><dt>Phone</dt><dd>{editor.record.phone || "—"}</dd></div><div><dt>District</dt><dd>{districtById[editor.record.districtId]?.name || "Unknown district"}</dd></div><div><dt>Duty station</dt><dd>{stationById[editor.record.dutyStationId]?.name || "Unknown station"}</dd></div></>}
          {editor.entity === "dutyStations" && <><div><dt>District</dt><dd>{districtById[editor.record.districtId]?.name || "Unknown district"}</dd></div><div><dt>Type</dt><dd>{editor.record.type} — {stationTypeLabel(editor.record.type)}</dd></div></>}
          <div><dt>Status</dt><dd>{editor.record.active ? "Active" : "Inactive"}</dd></div>
        </dl>}
      </Modal>

      <Modal open={Boolean(deleteTarget)} danger modalHeading={`Delete ${getSection(deleteTarget?.entity)?.singular || "record"}?`} primaryButtonText="Delete" secondaryButtonText="Cancel" onRequestSubmit={confirmDelete} onRequestClose={() => setDeleteTarget(null)}>
        <p>This permanently removes <strong>{deleteTarget ? getRecordName(deleteTarget.record) : "this record"}</strong> from the temporary administration store.</p>
      </Modal>
    </section>
  );
};

export default AdminDataManager;
